import { formatBirthdate, getPrices } from "../helpers";

import { Dispatch } from "redux";
import { FlattenedPlanData } from "../../../../hooks/useWellnessPlans";
import { ICardItemResponse } from "../../../../services/card-list/card-list.interface";
import { ICommonWPClass } from "../../../../services/get-alerts/get-alerts.interface";
import { IPetItemResponse } from "../../../../services/pet-list/pet-list.interface";
import { IScratchPayPurchaseData } from "../../../../services/scratchpay/scratchpay.interface";
import { PaymentMethodProvider } from "../../../../services/renewal-preference/renewal-preference.interface";
import { MESSAGES } from "../constants";
import { MIXPANEL_EVENTS } from "../../../../config/mixPanelEvents";
import { billCycle } from "../types";
import { getSpeciesName } from "../../../../components/PageTitle/WellnessPlanTitle/WellnessPlanTitle";
import { toastMessageActions } from "../../../../state/request";
import { userActivityLogger } from "../../../../utils/mixpanel";

interface IPollingContext {
  petId: string;
  petName: string;
  petImage?: string;
  planName: string;
}

interface IInitiatePurchaseParams {
  purchaseData: IScratchPayPurchaseData;
  pollingContext: IPollingContext;
}

interface ICreateSubscriptionParams {
  petId: string;
  cardId: string;
  planId: number;
}

import { IAutoRenewalPreferenceInput } from "../../../../services/renewal-preference/renewal-preference.interface";

type ISaveRenewalPreferenceParams<T extends PaymentMethodProvider = PaymentMethodProvider> =
  IAutoRenewalPreferenceInput<T>;

interface IPurchaseParams {
  petId: string;
  currentPet: IPetItemResponse[];
  billingCycle: billCycle;
  wellnessPurchaseData: FlattenedPlanData | null;
  prices: ReturnType<typeof getPrices>;
  cardSelected: ICardItemResponse;
  initiatePurchase: (params: IInitiatePurchaseParams) => void;
  createSubscription: (params: ICreateSubscriptionParams) => Promise<unknown>;
  saveRenewalPreference: <T extends PaymentMethodProvider>(params: ISaveRenewalPreferenceParams<T>) => Promise<unknown>;
  dispatch: Dispatch;
}

export const handleMonthlyPurchase = ({
  petId,
  currentPet,
  wellnessPurchaseData,
  prices,
  billingCycle,
  initiatePurchase,
  dispatch,
}: Pick<
  IPurchaseParams,
  "petId" | "currentPet" | "wellnessPurchaseData" | "prices" | "billingCycle" | "initiatePurchase" | "dispatch"
>) => {
  const scratchpayPlanId = wellnessPurchaseData?.scratchpayPlanId;

  if (!scratchpayPlanId || !Number.isInteger(scratchpayPlanId)) {
    dispatch(
      toastMessageActions.setGlobalAlert({
        severity: "error",
        message: MESSAGES.PAYMENT_INPUT_DATA_ERROR,
      }),
    );
    return;
  }

  if (currentPet?.[0]) {
    const formattedBirthdate = formatBirthdate(currentPet[0].birthdate);

    userActivityLogger(MIXPANEL_EVENTS.SCRATCHPAY_MONTHLY_SELECTED, {
      pet_id: petId,
      pet_name: currentPet[0].name,
      species: getSpeciesName(currentPet[0].species),
      plan_type: "Monthly",
      selected_plan: wellnessPurchaseData?.planType,
      dob: formattedBirthdate,
      payment_method: "ScratchPay",
    });
  }

  initiatePurchase({
    purchaseData: {
      planId: String(scratchpayPlanId),
      petId: Number(petId),
    },
    pollingContext: {
      petId: petId || "",
      petName: currentPet?.[0]?.name || "",
      petImage: currentPet?.[0]?.image,
      planName: wellnessPurchaseData?.planName || "",
    },
  });
};

export const handleRenewalPurchase = async ({
  petId,
  currentPet,
  billingCycle,
  wellnessPurchaseData,
  prices,
  cardSelected,
  renewalAlert,
  saveRenewalPreference,
  dispatch,
}: Pick<
  IPurchaseParams,
  | "petId"
  | "currentPet"
  | "billingCycle"
  | "wellnessPurchaseData"
  | "prices"
  | "cardSelected"
  | "saveRenewalPreference"
  | "dispatch"
> & { renewalAlert: ICommonWPClass }) => {
  if (currentPet?.[0]) {
    const formattedBirthdate = formatBirthdate(currentPet[0].birthdate);

    userActivityLogger(
      MIXPANEL_EVENTS.USER_WP_RENEWAL_SUBSCRIPTION_TYPE_SELECTION,
      {
        pet_id: petId,
        pet_name: currentPet[0].name,
        species: getSpeciesName(currentPet[0].species),
        planType: billingCycle === "perYear" ? "Annual" : "Monthly",
        selected_plan: wellnessPurchaseData?.planType,
        dob: formattedBirthdate,
      },
    );

    userActivityLogger(MIXPANEL_EVENTS.WP_RENEWAL_PURCHASE_TRIGGERED, {
      pet_id: petId,
      pet_name: currentPet[0].name,
      species: getSpeciesName(currentPet[0].species),
      wellnessPlanCode: wellnessPurchaseData?.planCode,
      plan_type: billingCycle === "perYear" ? "Annual" : "Monthly",
      selected_plan: wellnessPurchaseData?.planType,
      dob: formattedBirthdate,
    });

    userActivityLogger(MIXPANEL_EVENTS.WP_PURCHASE_TRIGGERED, {
      pet_id: petId,
      pet_name: currentPet[0].name,
      species: getSpeciesName(currentPet[0].species),
      wellnessPlanCode: wellnessPurchaseData?.planCode,
      plan_type: billingCycle === "perYear" ? "Annual" : "Monthly",
      selected_plan: wellnessPurchaseData?.planType,
      dob: formattedBirthdate,
      purchase_type: "renewal",
    });
  }

  const currentSubscriptionId = renewalAlert.subscriptionId;

  if (currentSubscriptionId) {
    if (currentPet?.[0]) {
      const formattedBirthdate = formatBirthdate(currentPet[0].birthdate);

      userActivityLogger(MIXPANEL_EVENTS.WP_RENEWAL_SAVE_PREFERENCE, {
        pet_id: petId,
        pet_name: currentPet[0].name,
        species: getSpeciesName(currentPet[0].species),
        wellnessPlanCode: wellnessPurchaseData?.planCode,
        plan_type: billingCycle === "perYear" ? "Annual" : "Monthly",
        selected_plan: wellnessPurchaseData?.planType,
        dob: formattedBirthdate,
      });
    }

    try {
      await saveRenewalPreference({
        currentSubscriptionId: currentSubscriptionId,
        newPlanId: Number(prices[billingCycle].planId),
        cardId: cardSelected.id,
        paymentMethodProvider: PaymentMethodProvider.SQUARE,
      });
    } catch {
      // Errors are surfaced via the hook's `error` state and rendered by the
      // unified error screen.
    }
  } else {
    if (currentPet?.[0]) {
      const formattedBirthdate = formatBirthdate(currentPet[0].birthdate);

      userActivityLogger(MIXPANEL_EVENTS.WP_RENEWAL_SAVE_PREFERENCE_ERROR, {
        pet_id: petId,
        pet_name: currentPet[0].name,
        species: getSpeciesName(currentPet[0].species),
        wellnessPlanCode: wellnessPurchaseData?.planCode,
        plan_type: billingCycle === "perYear" ? "Annual" : "Monthly",
        selected_plan: wellnessPurchaseData?.planType,
        dob: formattedBirthdate,
        error_message: "There was an error renewing subscription.",
      });
    }

    dispatch(
      toastMessageActions.setGlobalAlert({
        severity: "error",
        message: MESSAGES.RENEWAL_ERROR,
      }),
    );
  }
};

export const handleNormalPurchase = async ({
  petId,
  currentPet,
  billingCycle,
  wellnessPurchaseData,
  prices,
  cardSelected,
  createSubscription,
}: Pick<
  IPurchaseParams,
  | "petId"
  | "currentPet"
  | "billingCycle"
  | "wellnessPurchaseData"
  | "prices"
  | "cardSelected"
  | "createSubscription"
>) => {
  if (currentPet?.[0]) {
    const formattedBirthdate = formatBirthdate(currentPet[0].birthdate);

    userActivityLogger(MIXPANEL_EVENTS.USER_WP_SUBSCRIPTION_TYPE_SELECTION, {
      pet_id: petId,
      pet_name: currentPet[0].name,
      species: getSpeciesName(currentPet[0].species),
      planType: billingCycle === "perYear" ? "Annual" : "Monthly",
      selected_plan: wellnessPurchaseData?.planType,
      dob: formattedBirthdate,
    });

    userActivityLogger(MIXPANEL_EVENTS.WP_PURCHASE_TRIGGERED, {
      pet_id: petId,
      pet_name: currentPet[0].name,
      species: getSpeciesName(currentPet[0].species),
      wellnessPlanCode: wellnessPurchaseData?.planCode,
      plan_type: billingCycle === "perYear" ? "Annual" : "Monthly",
      selected_plan: wellnessPurchaseData?.planType,
      dob: formattedBirthdate,
    });
  }

  try {
    await createSubscription({
      petId: petId,
      cardId: cardSelected.id,
      planId: Number(prices[billingCycle].planId),
    });
  } catch {
    // Errors are surfaced via the hook's `error` state and rendered by the
    // unified error screen.
  }
};

export const handleMonthlyRenewalPurchase = async ({
  petId,
  currentPet,
  wellnessPurchaseData,
  prices,
  billingCycle,
  renewalAlert,
  saveRenewalPreference,
  dispatch,
  scratchpayPlanId,
}: Pick<
  IPurchaseParams,
  | "petId"
  | "currentPet"
  | "wellnessPurchaseData"
  | "prices"
  | "billingCycle"
  | "saveRenewalPreference"
  | "dispatch"
> & {
    renewalAlert: ICommonWPClass;
    scratchpayPlanId: number;
}) => {
  if (currentPet?.[0]) {
    const formattedBirthdate = formatBirthdate(currentPet[0].birthdate);

    userActivityLogger(
      MIXPANEL_EVENTS.USER_WP_RENEWAL_SUBSCRIPTION_TYPE_SELECTION,
      {
        pet_id: petId,
        pet_name: currentPet[0].name,
        species: getSpeciesName(currentPet[0].species),
        planType: "Monthly",
        selected_plan: wellnessPurchaseData?.planType,
        dob: formattedBirthdate,
        payment_method: "ScratchPay",
      },
    );

    userActivityLogger(MIXPANEL_EVENTS.WP_RENEWAL_PURCHASE_TRIGGERED, {
      pet_id: petId,
      pet_name: currentPet[0].name,
      species: getSpeciesName(currentPet[0].species),
      wellnessPlanCode: wellnessPurchaseData?.planCode,
      plan_type: "Monthly",
      selected_plan: wellnessPurchaseData?.planType,
      dob: formattedBirthdate,
      payment_method: "ScratchPay",
    });

    userActivityLogger(MIXPANEL_EVENTS.WP_PURCHASE_TRIGGERED, {
      pet_id: petId,
      pet_name: currentPet[0].name,
      species: getSpeciesName(currentPet[0].species),
      wellnessPlanCode: wellnessPurchaseData?.planCode,
      plan_type: billingCycle === "perYear" ? "Annual" : "Monthly",
      selected_plan: wellnessPurchaseData?.planType,
      dob: formattedBirthdate,
      purchase_type: "renewal",
      payment_method: "ScratchPay",
    });
  }

  const currentSubscriptionId = renewalAlert.subscriptionId;

  if (currentSubscriptionId) {
    if (currentPet?.[0]) {
      const formattedBirthdate = formatBirthdate(currentPet[0].birthdate);

      userActivityLogger(MIXPANEL_EVENTS.WP_RENEWAL_SAVE_PREFERENCE, {
        pet_id: petId,
        pet_name: currentPet[0].name,
        species: getSpeciesName(currentPet[0].species),
        wellnessPlanCode: wellnessPurchaseData?.planCode,
        plan_type: "Monthly",
        selected_plan: wellnessPurchaseData?.planType,
        dob: formattedBirthdate,
        payment_method: "ScratchPay",
      });
    }

    try {
      await saveRenewalPreference({
        currentSubscriptionId: currentSubscriptionId,
        scratchpayPlanId: scratchpayPlanId,
        paymentMethodProvider: PaymentMethodProvider.SCRATCH_PAY,
      });
    } catch {
      // Errors are surfaced via the hook's `error` state and rendered by the
      // unified error screen.
    }
  } else {
    if (currentPet?.[0]) {
      const formattedBirthdate = formatBirthdate(currentPet[0].birthdate);

      userActivityLogger(MIXPANEL_EVENTS.WP_RENEWAL_SAVE_PREFERENCE_ERROR, {
        pet_id: petId,
        pet_name: currentPet[0].name,
        species: getSpeciesName(currentPet[0].species),
        wellnessPlanCode: wellnessPurchaseData?.planCode,
        plan_type: "Monthly",
        selected_plan: wellnessPurchaseData?.planType,
        dob: formattedBirthdate,
        payment_method: "ScratchPay",
        error_message: "There was an error renewing subscription.",
      });
    }

    dispatch(
      toastMessageActions.setGlobalAlert({
        severity: "error",
        message: MESSAGES.RENEWAL_ERROR,
      }),
    );
  }
};

